import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner, Badge } from '../../../shared/components/ui';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { jobberApi } from '../../../shared/services/settingsApi';

function StatusBadge({ status }) {
  if (status === 'connected') return <Badge variant="green" size="sm">Connected</Badge>;
  if (status === 'disconnected') return <Badge variant="yellow" size="sm">Disconnected</Badge>;
  return <Badge variant="gray" size="sm">Not connected</Badge>;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Jobber connection card.
 *
 * The OAuth flow is browser-initiated: clicking Connect calls our backend
 * to mint an authorize URL, then we navigate the top-level browser to
 * Jobber. After the user grants access, Jobber redirects back to our
 * callback Lambda, which 302s back here with `?status=connected` (or error).
 * On mount we read those params and surface a banner.
 */
export function Integrations() {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [pendingDisconnect, setPendingDisconnect] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncBanner, setSyncBanner] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const callbackStatus = searchParams.get('status');
  const callbackMessage = searchParams.get('message');

  // `silent` skips toggling isLoading so the badge doesn't flicker during
  // background polls. The first load (and explicit user-triggered reloads)
  // pass silent=false to show the spinner.
  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const data = await jobberApi.status();
      setStatus(data);
    } catch (err) {
      setError(err.message || 'Failed to load Jobber status');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Background poll while a sync is running so the UI flips from
  // "Syncing..." back to "Sync now" automatically when the worker
  // finishes. No polling otherwise -- this endpoint is cheap but pointless
  // when nothing is changing.
  useEffect(() => {
    if (status?.lastSyncStatus !== 'running') return undefined;
    const id = setInterval(() => loadStatus(true), 5000);
    return () => clearInterval(id);
  }, [status?.lastSyncStatus, loadStatus]);

  // Clean callback params from the URL after we've read them once so a
  // refresh doesn't keep showing the banner.
  useEffect(() => {
    if (!callbackStatus) return;
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('message');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const { authorizeUrl } = await jobberApi.connect();
      if (!authorizeUrl) throw new Error('Server did not return an authorize URL');
      // Top-level navigation. Returning to our app will hit the callback
      // route handled server-side, which 302s back here.
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err.message || 'Failed to start Jobber authorization');
      setIsConnecting(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setError(null);
    setSyncBanner(null);
    try {
      await jobberApi.sync();
      setSyncBanner('Sync started. This can take a few minutes for large accounts.');
      // Refresh status so the lastSync row flips to "running" or whatever
      // the worker writes when it picks the message up.
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to start Jobber sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnectConfirmed = async () => {
    setPendingDisconnect(false);
    setError(null);
    try {
      await jobberApi.disconnect();
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Failed to disconnect Jobber');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Integrations</CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Connect external systems to import historical estimates and keep your data in sync.
          </p>
        </CardHeader>
      </Card>

      {/* OAuth callback banner */}
      {callbackStatus === 'connected' ? (
        <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm">
          Jobber connected successfully.
        </div>
      ) : null}
      {callbackStatus === 'error' ? (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
          {callbackMessage || 'Could not complete Jobber authorization.'}
        </div>
      ) : null}

      {/* Inline error banner */}
      {error ? (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      ) : null}

      {/* Sync-started banner */}
      {syncBanner ? (
        <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm">
          {syncBanner}
        </div>
      ) : null}

      {/* Cap-reached banner: shown after a sync that hit the per-tenant
          import cap. Status is still 'complete' (the sync finished without
          errors); this banner just nudges the user that there's more
          Jobber data they could pull. */}
      {!isLoading && status?.lastSyncCapReached ? (
        <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
          The last sync stopped at your import cap. Click <strong>Sync now</strong> again
          to pull the next batch, or contact support to raise your limit.
        </div>
      ) : null}

      <Card>
        <CardContent className="py-5">
          <div className="flex items-start gap-4">
            {/* Logo placeholder. Drop-in for a real Jobber wordmark later. */}
            <div className="flex-shrink-0 w-12 h-12 rounded-md bg-blue-600 text-white flex items-center justify-center font-bold">
              J
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Jobber</h3>
                {isLoading ? <Spinner size="sm" /> : <StatusBadge status={status?.status} />}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Pull historical quotes, invoices, jobs, and clients from your Jobber account.
                Read-only access — we never write back to Jobber.
              </p>

              {!isLoading && status?.connected ? (
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  {status.jobberAccountId ? (
                    <>
                      <dt className="font-medium text-gray-600 dark:text-gray-300">Account</dt>
                      <dd className="sm:text-right">{status.jobberAccountId}</dd>
                    </>
                  ) : null}
                  <dt className="font-medium text-gray-600 dark:text-gray-300">Connected at</dt>
                  <dd className="sm:text-right">{formatTimestamp(status.createdAt)}</dd>
                  <dt className="font-medium text-gray-600 dark:text-gray-300">Last sync</dt>
                  <dd className="sm:text-right">
                    {status.lastSyncAt ? formatTimestamp(status.lastSyncAt) : 'Never'}
                    {status.lastSyncStatus ? ` · ${status.lastSyncStatus}` : ''}
                  </dd>
                </dl>
              ) : null}
            </div>
            <div className="flex-shrink-0 flex flex-col gap-2 items-end">
              {isLoading ? null : status?.connected ? (
                <>
                  <Button
                    size="sm"
                    onClick={handleSync}
                    disabled={
                      isSyncing
                      || status?.lastSyncStatus === 'running'
                      || status?.lastSyncStatus === 'disabled'
                    }
                    title={
                      status?.lastSyncStatus === 'disabled'
                        ? 'Jobber imports are currently disabled. Contact support to enable.'
                        : status?.lastSyncStatus === 'running'
                        ? 'A sync is already running'
                        : 'Pull historical quotes, invoices, and jobs from Jobber'
                    }
                  >
                    {isSyncing
                      ? 'Starting…'
                      : status?.lastSyncStatus === 'disabled'
                      ? 'Imports disabled'
                      : status?.lastSyncStatus === 'running'
                        ? 'Syncing…'
                        : 'Sync now'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPendingDisconnect(true)}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={handleConnect} disabled={isConnecting}>
                  {isConnecting ? 'Redirecting…' : 'Connect Jobber'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={pendingDisconnect}
        title="Disconnect Jobber?"
        message="You'll need to re-authorize from scratch to reconnect. Imported data stays in your account."
        confirmLabel="Disconnect"
        confirmVariant="danger"
        onConfirm={handleDisconnectConfirmed}
        onCancel={() => setPendingDisconnect(false)}
      />
    </div>
  );
}

export default Integrations;
